import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Alert,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * PreviewModal - Shows preview of filter results before running full job
 * Matches the styling of FilterResults.js for consistency
 */
const PreviewModal = ({
  open,
  onClose,
  onConfirm,
  previewData,
  loading,
  error,
  creditCost
}) => {
  const [expandedColumn, setExpandedColumn] = useState(null);

  // Handle accordion expansion
  const handleAccordionChange = (column) => (event, isExpanded) => {
    setExpandedColumn(isExpanded ? column : null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper', // Uses theme background.paper (#161c3e)
          color: 'white',
          maxHeight: '90vh',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)', pb: 2 }}>
        <Typography variant="h6" component="div">
          Preview Results
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {/* Loading State */}
        {loading && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 6
          }}>
            <CircularProgress size={60} sx={{ color: '#0095FF' }} />
            <Typography variant="h6" sx={{ mt: 3 }}>
              Analyzing sample values...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This usually takes 5-15 seconds
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Results */}
        {previewData && !loading && (
          <>
            {/* Stats Cards - Match FilterResults styling */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 2,
              mb: 3
            }}>
              {/* Total Rows Card */}
              <Card sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid #ff980030',
                borderRadius: 3
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Total Rows
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#ff9800' }}>
                    {previewData.totalRows || 0}
                  </Typography>
                </CardContent>
              </Card>

              {/* Estimated Qualified Card */}
              <Card sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid #0095FF30',
                borderRadius: 3
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Est. Qualified
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                    <Typography variant="h4" sx={{ color: '#0095FF' }}>
                      ~{previewData.stats?.overall?.estimatedKeptPercent || 0}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    (~{previewData.stats?.overall?.estimatedKeptRows || 0} rows)
                  </Typography>
                </CardContent>
              </Card>

              {/* Estimated Disqualified Card */}
              <Card sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid #FF149330',
                borderRadius: 3
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Est. Disqualified
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                    <Typography variant="h4" sx={{ color: '#FF1493' }}>
                      ~{previewData.stats?.overall?.estimatedExcludedPercent || 0}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    (~{previewData.stats?.overall?.estimatedExcludedRows || 0} rows)
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Info Alert */}
            <Alert
              severity="info"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(0, 149, 255, 0.1)',
                border: '1px solid #0095FF30',
                '& .MuiAlert-icon': { color: '#0095FF' }
              }}
            >
              This is an estimate based on {previewData.sampleSize || 20} sample values per column. Actual results may vary.
            </Alert>

            {/* Sample Decisions by Column */}
            <Typography variant="h6" gutterBottom sx={{ color: '#0095FF' }}>
              Sample Decisions
            </Typography>

            <Stack spacing={1}>
              {previewData.decisions && Object.entries(previewData.decisions).map(([column, decisions]) => {
                const columnStats = previewData.stats?.columnStats?.[column] || {};
                const keptCount = decisions.filter(d => d.decision === 'KEEP').length;
                const excludedCount = decisions.filter(d => d.decision === 'EXCLUDE').length;

                return (
                  <Accordion
                    key={column}
                    expanded={expandedColumn === column}
                    onChange={handleAccordionChange(column)}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px !important',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        margin: 0
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                      sx={{
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.04)' },
                        borderRadius: '12px'
                      }}
                    >
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        pr: 2
                      }}>
                        <Typography sx={{ fontWeight: 500, color: '#FF1493' }}>
                          {column}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            size="small"
                            label={`${keptCount} Qualified`}
                            sx={{
                              bgcolor: 'rgba(0, 149, 255, 0.1)',
                              color: '#0095FF',
                              fontWeight: 500,
                              border: '1px solid #0095FF30',
                              height: '24px',
                              fontSize: '0.75rem'
                            }}
                          />
                          <Chip
                            size="small"
                            label={`${excludedCount} Disqualified`}
                            sx={{
                              bgcolor: 'rgba(255, 20, 147, 0.1)',
                              color: '#FF1493',
                              fontWeight: 500,
                              border: '1px solid #FF149330',
                              height: '24px',
                              fontSize: '0.75rem'
                            }}
                          />
                          {columnStats.totalUniqueValues > decisions.length && (
                            <Chip
                              size="small"
                              label={`${columnStats.totalUniqueValues} total unique`}
                              sx={{
                                bgcolor: 'rgba(255, 152, 0, 0.1)',
                                color: '#ff9800',
                                border: '1px solid #ff980030',
                                height: '24px',
                                fontSize: '0.75rem'
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                      {/* Column Headers */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 80px 100px 2fr',
                        gap: 1.5,
                        p: 1.5,
                        mb: 1,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid rgba(255, 255, 255, 0.05)'
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Value</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem', textAlign: 'center' }}>Count</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem', textAlign: 'center' }}>Decision</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Reason</Typography>
                      </Box>

                      {/* Decision Rows */}
                      <Stack spacing={0.5}>
                        {decisions.map((decision, index) => (
                          <Paper
                            key={index}
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.02)',
                              border: '1px solid rgba(255, 255, 255, 0.05)',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                              }
                            }}
                          >
                            <Box sx={{
                              display: 'grid',
                              gridTemplateColumns: '2fr 80px 100px 2fr',
                              gap: 1,
                              alignItems: 'center'
                            }}>
                              {/* Value */}
                              <Tooltip title={decision.value} arrow placement="top">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.8rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'help'
                                  }}
                                >
                                  {decision.value}
                                </Typography>
                              </Tooltip>

                              {/* Frequency */}
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                                {decision.frequency || 0}
                              </Typography>

                              {/* Decision Chip */}
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Chip
                                  label={decision.decision === 'KEEP' ? 'Qualified' : 'Disqualified'}
                                  size="small"
                                  sx={{
                                    backgroundColor: decision.decision === 'KEEP'
                                      ? 'rgba(0, 149, 255, 0.1)'
                                      : 'rgba(255, 20, 147, 0.1)',
                                    color: decision.decision === 'KEEP' ? '#0095FF' : '#FF1493',
                                    fontWeight: 500,
                                    border: decision.decision === 'KEEP'
                                      ? '1px solid #0095FF30'
                                      : '1px solid #FF149330',
                                    height: '20px',
                                    fontSize: '0.65rem'
                                  }}
                                />
                              </Box>

                              {/* Reason */}
                              <Tooltip title={decision.reason || ''} arrow placement="top">
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'help',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {decision.reason}
                                </Typography>
                              </Tooltip>
                            </Box>
                          </Paper>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{
        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        px: 3,
        py: 2,
        justifyContent: 'space-between'
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: 'text.secondary',
            textTransform: 'none',
            borderRadius: '4px',
            '&:hover': {
              borderColor: '#0095FF',
              color: '#0095FF',
              backgroundColor: 'rgba(0, 149, 255, 0.1)'
            }
          }}
        >
          Adjust Criteria
        </Button>

        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading || error}
          sx={{
            minWidth: 180,
            textTransform: 'none',
            borderRadius: '4px'
          }}
        >
          {creditCost ? `Run Full Job (${creditCost} credits)` : 'Run Full Job'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewModal;
